import { Box } from '@mui/material'
// @ts-ignore
import MuxPlayer from '@mux/mux-player-react'
import type MuxPlayerElement from '@mux/mux-player'
import { forwardRef, useRef, useImperativeHandle, useEffect, useMemo } from 'react'
import { useMuxTrimEnforcement } from '@app/helpers/hooks/useMuxTrimEnforcement'
import { MuxSettingsMenu } from './MuxSettingsMenu'

export interface MuxVideoPlayerWithTrimProps {
  playbackId?: string
  startTime?: number
  endTime?: number
  extraSourceParams?: Record<string, unknown>
  thumbnailTime?: number
  disableTracking?: boolean
  accentColor?: string
  autoPlay?: boolean
  className?: string
  muted?: boolean
  showCaptions?: boolean
  subtitleTrackId?: string
  videoTitle?: string
}

export const MuxVideoPlayerWithTrim = forwardRef<MuxPlayerElement, MuxVideoPlayerWithTrimProps>(
  (
    {
      playbackId,
      startTime,
      endTime,
      extraSourceParams,
      thumbnailTime,
      disableTracking = false,
      accentColor,
      autoPlay = false,
      muted = false,
      className,
      showCaptions = false,
      subtitleTrackId,
      videoTitle,
    },
    ref,
  ) => {
    const internalRef = useRef<MuxPlayerElement>(null)

    // Expose the internal ref through the forwarded ref
    useImperativeHandle(ref, () => internalRef.current!, [])

    const accessibleVideoLabel = useMemo(() => {
      const trimmedTitle = videoTitle.trim()
      return trimmedTitle ? `${trimmedTitle} Video` : 'Video'
    }, [videoTitle])

    // Use the trim enforcement hook to handle start/end time boundaries
    useMuxTrimEnforcement({
      playerRef: internalRef,
      trimData:
        startTime !== undefined || endTime !== undefined
          ? {
              start: startTime,
              end: endTime,
            }
          : undefined,
    })

    // Prevent AUTO-HIDING of the controls in MUX player
    useEffect(() => {
      const player = internalRef.current
      if (!player) return null

      const setup = () => {
        const mediaController = player.shadowRoot
          ?.querySelector('media-theme')
          ?.shadowRoot?.querySelector('media-controller')
        if (!mediaController) return

        // Setting autohide=-1 disables the autohide timer completely
        mediaController.setAttribute('autohide', '-1')
        mediaController.removeAttribute('userinactive')
      }

      setup()
      player.addEventListener('loadedmetadata', setup)
      return () => player.removeEventListener('loadedmetadata', setup)
    }, [])

    return (
      <Box 
          role="group" 
          aria-label={accessibleVideoLabel}
          tabIndex={0}
          sx={{ width: '100%', position: 'relative' }} 
          className={className}>
        <MuxPlayer
          metadataVideoTitle={videoTitle}
          ref={internalRef}
          playbackId={playbackId}
          startTime={startTime}
          extraSourceParams={extraSourceParams}
          thumbnailTime={thumbnailTime}
          disableTracking={disableTracking}
          accent-color={accentColor}
          autoPlay={autoPlay}
          muted={muted}
          className={className}
          defaultHiddenCaptions={!showCaptions}
          style={
            {
              display: 'block',
              '--seek-backward-button': 'none',
              '--seek-forward-button': 'none',
              '--rendition-menu-button': 'none',
              '--playback-rate-button': 'none',
              '--pip-button': 'none',
              '--time-display': 'none',
              '--duration-display': 'none',
              ...(!showCaptions ? { '--captions-button': 'none' } : {}),
            } as React.CSSProperties
          }
        />

        {/* Settings menu button - positioned in the control bar area */}
        {showCaptions && subtitleTrackId && playbackId && (
          <MuxSettingsMenu playbackId={playbackId} subtitleTrackId={subtitleTrackId} />
        )}
      </Box>
    )
  },
)

MuxVideoPlayerWithTrim.displayName = 'MuxVideoPlayerWithTrim'
